/**
 * Design System Constants for PromptManagerDashboard
 *
 * This file contains all design tokens, constants, and styling patterns
 * used across the PromptManagerDashboard components.
 */

// ============================================================================
// SPACING SYSTEM
// ============================================================================
export const SPACING = {
  // Base spacing units (based on 8px grid)
  xs: 0.5, // 4px
  sm: 1, // 8px
  md: 1.5, // 12px
  lg: 2, // 16px
  xl: 2.5, // 20px
  xxl: 3, // 24px
  xxxl: 4, // 32px

  // Component-specific spacing
  cardPadding: 1.5, // 12px
  sectionMargin: 4, // 32px
  componentGap: 2, // 16px
  headerGap: 2, // 16px
  iconSpacing: 2, // 16px
  tableCellPadding: 1.5, // 12px
  accordionPadding: 2, // 16px
};

// ============================================================================
// COMPONENT SIZING STANDARDS
// ============================================================================
export const COMPONENT_SIZES = {
  // Button sizes
  button: {
    minWidth: 80, // Minimum button width in px
    maxWidth: 200, // Maximum button width in px
    small: {
      minWidth: 64,
      maxWidth: 150,
      height: 32,
      paddingX: 1.5, // 12px
    },
    medium: {
      minWidth: 80,
      maxWidth: 200,
      height: 40,
      paddingX: 2, // 16px
    },
    large: {
      minWidth: 100,
      maxWidth: 250,
      height: 48,
      paddingX: 3, // 24px
    },
  },
  // Chip sizes
  chip: {
    minWidth: 40,
    maxWidth: 120,
    height: {
      small: 24,
      medium: 32,
    },
  },
  // Select/TextField sizes
  select: {
    minWidth: 120,
    maxWidth: 300,
    height: {
      small: 32,
      medium: 40,
    },
  },
  // Label sizes
  label: {
    minWidth: 60,
    maxWidth: 150,
  },
};

// ============================================================================
// TYPOGRAPHY SYSTEM
// ============================================================================
export const TYPOGRAPHY = {
  // Font weights
  weights: {
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    extraBold: 800,
  },

  // Font sizes and variants
  variants: {
    // Headers
    h1: { variant: 'h1', fontWeight: 700, fontSize: '2.5rem', lineHeight: 1.2 },
    h2: { variant: 'h2', fontWeight: 700, fontSize: '2rem', lineHeight: 1.3 },
    h3: {
      variant: 'h3',
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: 1.3,
    },
    h4: { variant: 'h4', fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.4 },
    h5: {
      variant: 'h5',
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h6: {
      variant: 'h6',
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },

    // Body text
    body1: {
      variant: 'body1',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      variant: 'body2',
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    bodyMedium: {
      variant: 'body1',
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.5,
    },

    // Small text
    caption: {
      variant: 'caption',
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.4,
    },
    captionBold: {
      variant: 'caption',
      fontWeight: 600,
      fontSize: '0.75rem',
      lineHeight: 1.4,
    },

    // Special text
    value: {
      variant: 'h3',
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.2,
    },
    label: {
      variant: 'caption',
      fontWeight: 600,
      fontSize: '0.75rem',
      lineHeight: 1.4,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
  },
};

// ============================================================================
// COLOR SYSTEM
// ============================================================================
export const COLORS = {
  // Primary color variants
  primary: {
    main: 'primary.main',
    light: 'primary.light',
    dark: 'primary.dark',
    bg: 'primary.50',
    text: 'primary.contrastText',
  },

  // Secondary colors
  secondary: {
    main: 'secondary.main',
    light: 'secondary.light',
    dark: 'secondary.dark',
    bg: 'secondary.50',
    text: 'secondary.contrastText',
  },

  // Status colors
  success: {
    main: 'success.main',
    light: 'success.light',
    dark: 'success.dark',
    bg: 'success.50',
    text: 'success.contrastText',
  },

  warning: {
    main: 'warning.main',
    light: 'warning.light',
    dark: 'warning.dark',
    bg: 'warning.50',
    text: 'warning.contrastText',
  },

  error: {
    main: 'error.main',
    light: 'error.light',
    dark: 'error.dark',
    bg: 'error.50',
    text: 'error.contrastText',
  },

  info: {
    main: 'info.main',
    light: 'info.light',
    dark: 'info.dark',
    bg: 'info.50',
    text: 'info.contrastText',
  },

  // Neutral colors
  neutral: {
    primary: 'text.primary',
    secondary: 'text.secondary',
    disabled: 'text.disabled',
    background: 'background.paper',
    surface: 'background.default',
  },
};

// ============================================================================
// COMPONENT STYLES
// ============================================================================
export const COMPONENT_STYLES = {
  // Card styles
  card: {
    base: {
      borderRadius: 2,
      boxShadow: 1,
      overflow: 'hidden',
    },
    metric: {
      height: '100%',
      minHeight: 160,
      position: 'relative',
    },
    insight: {
      bgcolor: 'primary.50',
    },
    accordion: {
      boxShadow: 2,
      borderRadius: 2,
      '&:before': {
        display: 'none',
      },
    },
  },

  // Icon styles
  icon: {
    section: {
      fontSize: 32,
      marginRight: SPACING.componentGap,
    },
    metric: {
      fontSize: 40,
    },
    small: {
      fontSize: 20,
    },
    status: {
      fontSize: 16,
    },
  },

  // Button styles
  button: {
    primary: {
      borderRadius: 2,
      textTransform: 'none',
      fontWeight: 600,
      px: 3,
      py: 1,
      minWidth: COMPONENT_SIZES.button.minWidth,
      maxWidth: COMPONENT_SIZES.button.maxWidth,
    },
    secondary: {
      borderRadius: 2,
      textTransform: 'none',
      fontWeight: 500,
      px: 2,
      py: 0.5,
      minWidth: COMPONENT_SIZES.button.minWidth,
      maxWidth: COMPONENT_SIZES.button.maxWidth,
    },
  },

  // Table styles
  table: {
    container: {
      borderRadius: 2,
      overflow: 'hidden',
    },
    cell: {
      padding: SPACING.tableCellPadding,
      borderBottom: '1px solid',
      borderBottomColor: 'divider',
    },
    header: {
      backgroundColor: 'grey.50',
      fontWeight: 600,
    },
  },

  // Accordion styles
  accordion: {
    summary: {
      minHeight: 64,
      '&.Mui-expanded': {
        minHeight: 64,
      },
    },
    details: {
      padding: SPACING.accordionPadding,
      paddingTop: 0,
    },
  },

  // Progress bar styles
  progress: {
    success: {
      backgroundColor: 'rgba(0,0,0,0.1)',
      '& .MuiLinearProgress-bar': {
        backgroundColor: '#4caf50',
      },
    },
    warning: {
      backgroundColor: 'rgba(0,0,0,0.1)',
      '& .MuiLinearProgress-bar': {
        backgroundColor: '#ff9800',
      },
    },
    error: {
      backgroundColor: 'rgba(0,0,0,0.1)',
      '& .MuiLinearProgress-bar': {
        backgroundColor: '#f44336',
      },
    },
  },
};

// ============================================================================
// LAYOUT CONSTANTS
// ============================================================================
export const LAYOUT = {
  // Breakpoints
  breakpoints: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },

  // Grid sizes
  grid: {
    xs12: { xs: 12 },
    sm6: { sm: 6 },
    md4: { md: 4 },
    md6: { md: 6 },
    md8: { md: 8 },
    lg3: { lg: 3 },
    lg4: { lg: 4 },
    lg6: { lg: 6 },
    lg8: { lg: 8 },
  },

  // Container max widths
  container: {
    maxWidth: 'xl',
    padding: SPACING.md,
  },

  // Flex layouts
  flex: {
    center: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    between: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    start: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    column: {
      display: 'flex',
      flexDirection: 'column',
    },
  },
};

// ============================================================================
// ANIMATION CONSTANTS
// ============================================================================
export const ANIMATIONS = {
  transitions: {
    standard: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    enter: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    leave: 'all 0.2s cubic-bezier(0.4, 0, 0.6, 1)',
  },

  durations: {
    short: 150,
    standard: 300,
    long: 500,
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get color styles for a given color name
 * @param {string} colorName - Color name (primary, success, warning, etc.)
 * @returns {object} Color styles object
 */
export const getColorStyles = colorName => {
  const colorConfig = COLORS[colorName] || COLORS.primary;
  return {
    color: colorConfig.main,
    bgcolor: colorConfig.bg,
  };
};

/**
 * Get typography styles for a given variant
 * @param {string} variant - Typography variant name
 * @returns {object} Typography styles object
 */
export const getTypographyStyles = variant => {
  return TYPOGRAPHY.variants[variant] || TYPOGRAPHY.variants.body1;
};

/**
 * Create consistent spacing object
 * @param {string|number} value - Spacing value
 * @returns {object} Spacing styles object
 */
export const spacing = value => ({
  margin: value,
  padding: value,
});

/**
 * Create consistent gap spacing
 * @param {string|number} value - Gap value
 * @returns {object} Gap styles object
 */
export const gap = value => ({
  gap: value,
});

// ============================================================================
// COMPONENT CONFIGURATIONS
// ============================================================================

/**
 * Default props for MetricCard component
 */
export const METRIC_CARD_DEFAULTS = {
  color: 'primary',
  minHeight: 160,
  iconSize: 40,
  valueVariant: 'h3',
  titleVariant: 'caption',
  subtitleVariant: 'body2',
};

/**
 * Default props for StatBox component
 */
export const STAT_BOX_DEFAULTS = {
  color: 'primary',
  valueVariant: 'h5',
  labelVariant: 'caption',
  minHeight: 70,
  borderRadius: 1.5,
};

/**
 * Default props for Accordion components
 */
export const ACCORDION_DEFAULTS = {
  defaultExpanded: false,
  elevation: 2,
  square: true,
  disableGutters: true,
};

/**
 * Default props for Table components
 */
export const TABLE_DEFAULTS = {
  size: 'medium',
  stickyHeader: true,
  sx: COMPONENT_STYLES.table.container,
};

/**
 * Status indicator configurations
 */
export const STATUS_CONFIG = {
  available: {
    color: 'success',
    label: 'Available',
    icon: '●',
  },
  disabled: {
    color: 'error',
    label: 'Disabled',
    icon: '●',
  },
  pending: {
    color: 'warning',
    label: 'Pending',
    icon: '●',
  },
  error: {
    color: 'error',
    label: 'Error',
    icon: '●',
  },
};

// ============================================================================
// SHARED STYLING PATTERNS (For Duplicated Code)
// ============================================================================

/**
 * Common Typography Patterns
 * Use these instead of inline fontWeight and color props
 */
export const SHARED_TYPOGRAPHY = {
  // Headers
  h3Bold: { fontWeight: 'bold' },
  h4Bold: { fontWeight: 'bold' },
  h5Bold: { fontWeight: 'bold' },

  // Body text
  bodyBold: { fontWeight: 'bold' },
  bodyMedium: { fontWeight: 500 },
  bodySecondary: { color: 'text.secondary' },
  bodyMediumSecondary: { color: 'text.secondary', fontWeight: 500 },

  // Caption text
  captionSecondary: { color: 'text.secondary' },
  captionBold: { fontWeight: 600 },

  // Labels
  labelUppercase: {
    color: 'text.secondary',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: 600,
    mt: 1,
  },
};

/**
 * Common Progress Bar Patterns
 * Use these instead of duplicating LinearProgress styling
 */
export const SHARED_PROGRESS = {
  // Small progress bar (60px wide)
  small: {
    width: 60,
    height: 6,
    borderRadius: 3,
    bgcolor: 'rgba(0,0,0,0.1)',
  },

  // Base progress bar (full width)
  base: {
    height: 8,
    borderRadius: 4,
    bgcolor: 'rgba(0,0,0,0.1)',
  },

  // Dynamic success rate colors
  getSuccessRateColor: rate => ({
    '& .MuiLinearProgress-bar': {
      bgcolor: rate >= 95 ? '#4caf50' : rate >= 90 ? '#ff9800' : '#f44336',
    },
  }),

  // Color palette for multiple progress bars
  colorPalette: [
    '#1976d2',
    '#4caf50',
    '#ff9800',
    '#f44336',
    '#9c27b0',
    '#00bcd4',
  ],
  getColorByIndex: index => ({
    '& .MuiLinearProgress-bar': {
      bgcolor:
        SHARED_PROGRESS.colorPalette[
          index % SHARED_PROGRESS.colorPalette.length
        ],
    },
  }),
};

/**
 * Common Status Indicator Patterns
 * Use these instead of duplicating status dot styling
 */
export const SHARED_STATUS = {
  // Status dot
  dot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
  },

  // Available status
  available: {
    bgcolor: 'success.main',
    boxShadow: '0 0 8px rgba(76, 175, 80, 0.6)',
  },

  // Disabled status
  disabled: {
    bgcolor: 'error.main',
    boxShadow: '0 0 8px rgba(244, 67, 54, 0.6)',
  },

  // Status container
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
  },

  // Status text styles
  textAvailable: {
    fontWeight: 500,
    color: 'success.main',
  },
  textDisabled: {
    fontWeight: 500,
    color: 'error.main',
  },
};

/**
 * Common Layout Patterns
 * Use these instead of duplicating flex layouts
 */
export const SHARED_LAYOUT = {
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  flexBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  flexStart: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  cardFullHeight: { height: '100%', display: 'flex', flexDirection: 'column' },
  cardContentFlex: { flex: 1, display: 'flex', flexDirection: 'column' },
};

// ============================================================================
// UTILITY FUNCTIONS FOR SHARED PATTERNS
// ============================================================================

/**
 * Get complete progress bar styles with success rate color
 * @param {number} successRate - The success rate percentage
 * @returns {object} Complete progress bar styles
 */
export const getProgressStyles = successRate => ({
  ...SHARED_PROGRESS.small,
  ...SHARED_PROGRESS.getSuccessRateColor(successRate),
});

/**
 * Get progress bar styles with color by index
 * @param {number} index - The index for color selection
 * @returns {object} Complete progress bar styles
 */
export const getProgressStylesByIndex = index => ({
  ...SHARED_PROGRESS.base,
  ...SHARED_PROGRESS.getColorByIndex(index),
});

/**
 * Get complete status styles
 * @param {boolean} isAvailable - Whether the status is available
 * @returns {object} Complete status styles
 */
export const getStatusStyles = isAvailable => ({
  ...SHARED_STATUS.dot,
  ...(isAvailable ? SHARED_STATUS.available : SHARED_STATUS.disabled),
});

/**
 * Get status text styles
 * @param {boolean} isAvailable - Whether the status is available
 * @returns {object} Status text styles
 */
export const getStatusTextStyles = isAvailable => ({
  fontWeight: 500,
  ...(isAvailable ? SHARED_STATUS.textAvailable : SHARED_STATUS.textDisabled),
});

/**
 * Format category name for display
 * @param {string} category - The category name
 * @returns {string} Formatted category name
 */
export const formatCategoryName = category => {
  return category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const designSystem = {
  SPACING,
  TYPOGRAPHY,
  COLORS,
  COMPONENT_STYLES,
  LAYOUT,
  ANIMATIONS,
  METRIC_CARD_DEFAULTS,
  STAT_BOX_DEFAULTS,
  ACCORDION_DEFAULTS,
  TABLE_DEFAULTS,
  STATUS_CONFIG,
  SHARED_TYPOGRAPHY,
  SHARED_PROGRESS,
  SHARED_STATUS,
  SHARED_LAYOUT,
  getColorStyles,
  getTypographyStyles,
  spacing,
  gap,
  getProgressStyles,
  getProgressStylesByIndex,
  getStatusStyles,
  getStatusTextStyles,
  formatCategoryName,
};

export default designSystem;
